import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography
} from '@mui/material';
import { FieldControl, MetaDrivenTable, PaginationControls, StatusChip, DialogBox } from '../../../shared/components';
import { useOperationHistory } from '../hooks/useOperationHistory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useState } from 'react';
import { useMarkAsCompleted } from '../hooks/useMarkAsCompleted';

export const OperationHistory: React.FC<{ projectId: string | undefined, activityId: string }> = ({ projectId, activityId }) => {

  const headers = [
    { "alias_name": "due_date", "label": "Due Date", "display_order": 1, is_show: true },
    { "alias_name": "completed_date", "label": "Completed Date", "display_order": 2, is_show: true },
    { "alias_name": "status_display_text", "label": "Status", "display_order": 3, is_show: true },
    { "alias_name": "actions", "label": "Actions", "display_order": 4, is_show: true },
  ];

  const {
    filter,
    setFilter,
    isError,
    error: fetchError,
    pagiantion,
    eventHistory,
    isLoading,
    handlePageChange,
    handleRowsPerPageChange,
    refetch,
  } = useOperationHistory({ projectId, activityId });

  const [selectedEvent, setSelectedEvent] = useState<null | any>(null);

  const {
    isOpen,
    setIsOpen,
    error,
    setError,
    loading,
    completeNotes,
    setCompleteNotes,
    handleMarkAsCompleted
  } = useMarkAsCompleted({ projectId, refetch });

  if (isLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    )
  };

  if (isError) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <Typography variant="h6" color="error">
          {fetchError?.message || "Error loading data."}
        </Typography>
      </Box>
    )
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} mb={2} gap={1} mr={2}>
        <Chip label={'All'} color={filter === 'All' ? 'primary' : 'default'} onClick={() => setFilter('All')} sx={{ cursor: 'pointer' }} />
        <Chip label={'Completed'} color={filter === 'Completed' ? 'primary' : 'default'} onClick={() => setFilter('Completed')} sx={{ cursor: 'pointer' }} />
        <Chip label={'Overdue'} color={filter === 'Overdue' ? 'primary' : 'default'} onClick={() => setFilter('Overdue')} sx={{ cursor: 'pointer' }} />
        <Chip label={'Pending'} color={filter === 'Pending' ? 'primary' : 'default'} onClick={() => setFilter('Pending')} sx={{ cursor: 'pointer' }} />
      </Box>

      <MetaDrivenTable
        headers={headers}
        data={eventHistory || []}
        dataMetrics={{
          status_display_text: {
            type: 'component',
            element: (value: { status: string; status_display_text: string }) => (
              <StatusChip label={value.status_display_text} color={value.status === 'Completed' ? 'green' : 'red'} />
            )
          },
          due_date: {
            type: 'component',
            element: (value: { due_date: string }) => (
              <Typography variant="inherit">{value.due_date}</Typography>
            )
          },
          completed_date: {
            type: 'component',
            element: (value: { due_date: string }) => (
              <Typography variant="inherit">{value.due_date}</Typography>
            )
          },
          actions: {
            type: 'component',
            element: (value: { due_date: string, status: string }) => (
              <IconButton
                onClick={() => {
                  setSelectedEvent(value);
                  setIsOpen(true);
                }}
                disabled={value.status === 'Completed'}
              >
                <CheckCircleOutlineIcon color={value.status === 'Completed' ? 'disabled' : 'success'} />
              </IconButton>
            )
          }
        }}
        sortDisabled
      />

      <PaginationControls
        total={pagiantion?.total || 0}
        rowsPerPage={pagiantion?.limit || 0}
        currentPage={pagiantion?.page || 0}
        handleRowsPerPage={handleRowsPerPageChange}
        handlePageChange={handlePageChange}
      />

      <DialogBox
        showModal={isOpen}
        title={'Mark Operation as Completed'}
        subTitle={''}
        onModalClose={() => {
          setIsOpen(false);
          setCompleteNotes("");
        }}
        actions={[
          {
            label: 'Close',
            variant: 'text',
            handleClick: () => {
              setIsOpen(false);
              setCompleteNotes("");
            },
          },
          {
            label: 'Mark as Completed',
            disabled: selectedEvent?.status === 'Completed',
            variant: 'contained',
            loading: loading,
            handleClick: async () => {
              if (selectedEvent.due_date) {
                await handleMarkAsCompleted(activityId, selectedEvent.due_date);
              }
            },
          }
        ]}
        children={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2">
              Please provide completion notes for this operation:
            </Typography>
            <FieldControl
              keyName={'notes'}
              label={'Notes'}
              value={completeNotes}
              multiline
              rows={4}
              error={!!error}
              helperText={error || ''}
              variant={'input'}
              onChange={(_key: string, value: string) => setCompleteNotes(value)}
            />
          </Box>
        }
      />

    </Box>
  );
};